import React, { useState } from 'react';
import { Row, Col, Button, Tooltip, Modal } from 'antd'
import { formatTriMergePricing, hasTriMergePricing } from './creditPricingUtils'
import './CreditAccountList.css'

const CreditAccountList = ({
    mode = 'personal',
    accountList,
    handleAccount,
    connectAccount,
    borrowerPaidPolicy,
    onOpenCreditPullPayment,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedAccount, setSelectedAccount] = useState(null)
    const vendorsList = {
        'KCBCredit': 'KCB Credit',
        'AdvantageCredit': 'Advantage Credit',
        'CreditTechnology': 'Credit Technology',
        'CreditTechnologies': 'Credit Technologies',
        'PremiumCreditBureau': 'Premium Credit Bureau',
        'PremiumCreditDataCDC': 'Premium Credit Data (CDC)',
        'Xactus': 'Xactus',
    }
    const borrowerPaidEnabled = Boolean(borrowerPaidPolicy?.Enabled)
    const getAccountName = (account, index) => {
        if (account.isAdminConfig) {
            if (mode === 'personal') {
                return 'Team credit account'
            }
            return accountList.length <= 1 ? 'Team credit account' : `Team credit account ${index + 1}`
        }
        return `My credit account`
    }
    const showRemoveModal = (account) => {
        setSelectedAccount(account)
        setIsModalOpen(true)
    }
    
    const handleRemove = () => {
        handleAccount(selectedAccount, 'remove')
        setIsModalOpen(false)
        setSelectedAccount(null)
    }
    
    const handleCancel = () => {
        setIsModalOpen(false)
        setSelectedAccount(null)
    }

    const renderBorrowerPaidBadge = (account) => {
        if (account.Type !== 'HardPull') return null
        if (mode === 'personal' && account.isAdminConfig && borrowerPaidEnabled && hasTriMergePricing(account)) {
            return <div className="borrower-paid-badge available">Borrower-paid available</div>
        }
        if (mode === 'personal' && !account.isAdminConfig) {
            return <div className="borrower-paid-badge unavailable">Borrower-paid unavailable</div>
        }
        return null
    }

    return (
        <div className="credit-report-setup">
            <Row gutter={20} className="credit-account-list">
                {
                    accountList.map((item, index) => {
                        const showPricing = item.Type === 'HardPull' && (mode === 'team' || borrowerPaidEnabled)
                        const pricingText = showPricing ? formatTriMergePricing(item) : null
                        return (
                        <Col lg={12} span={24} key={item.originalIndex ?? `${item.Type}-${item.Username}-${index}`}>
                            <div className='credit-account-item'>
                                <div className='account-name'>
                                    <img src='/images/user-account-icon.svg' alt="" />
                                    <div>{getAccountName(item, index)}</div>
                                </div>
                                <div className='account-info'>
                                    <div className='account-info-label'>Credit vendor:</div>
                                    <div className='account-info-value'>{vendorsList[item.Vendor] || item.Vendor}</div>
                                </div>
                                <div className='account-info'>
                                    <div className='account-info-label'>User name:</div>
                                    <div className='account-info-value'>{item.Username}</div>
                                </div>
                                {pricingText && (
                                    <div className='account-info'>
                                        <div className='account-info-label'>Pricing:</div>
                                        <div className='account-info-value'>{pricingText}</div>
                                    </div>
                                )}
                                {renderBorrowerPaidBadge(item)}
                                <div className="account-settings">
                                    {mode === 'personal' && !item.isAdminConfig && item.Type === 'HardPull' && (
                                        <div className="account-personal-paid-hint">Accounts you connect yourself always run as LO paid.</div>
                                    )}
                                    <div className="account-settings-actions">
                                    <div className='account-settings-btn'>
                                        {
                                            item.isAdminConfig && mode === 'personal' ? (
                                                <>
                                                    <Tooltip 
                                                        align={{ offset: [-20, -8] }}
                                                        overlayClassName='account-lock-tooltip' 
                                                        placement="topLeft" 
                                                        title="This account is configured and managed by your administrator. Changes cannot be made without admin privileges."
                                                    >
                                                        <span className="account-lock-label">
                                                            <img src='/images/user-account-lock.svg' alt="" />
                                                            <span>Set by your admin</span>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Button className='account-default-btn' onClick={() => { handleAccount(item, 'edit') }}>Edit</Button>
                                                    <Button 
                                                        className='account-text-btn' 
                                                        type="text" 
                                                        onClick={() => showRemoveModal(item)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </>
                                            )
                                        }
                                    </div>
                                    <div className="account-settings-right">
                                        {mode === 'personal' && (
                                            <Button className='account-text-btn' disabled={item.IsDefault} type="text" onClick={() => { handleAccount(item, 'setDefault') }}>Set as default</Button>
                                        )}
                                        {mode === 'personal' && item.isAdminConfig && item.Type === 'HardPull' && borrowerPaidEnabled && hasTriMergePricing(item) && (
                                            <Button
                                                className='account-text-btn'
                                                type="text"
                                                onClick={() => onOpenCreditPullPayment?.(item)}
                                            >
                                                Credit pull payment
                                            </Button>
                                        )}
                                    </div>
                                    </div>
                                </div>
                                {
                                    item.IsDefault && mode === 'personal' && (
                                        <div className='default-tag'>Default</div>
                                    )
                                }
                            </div>
                        </Col>
                        )
                    })
                }
            </Row>
            <Modal
                className='remove-account-modal'
                title="Remove connected account"
                centered
                open={isModalOpen}
                onCancel={handleCancel}
                styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                footer={[
                    <Button className='remove-account-modal-cancel-btn' key="cancel" onClick={handleCancel}>
                        No, thanks
                    </Button>,
                    <Button
                        className='remove-account-modal-remove-btn'
                        key="remove"
                        type="primary"
                        onClick={handleRemove}
                        danger
                    >
                        Remove
                    </Button>
                ]}
                width={410}
            >
                <div className='remove-account-modal-content'>Are you sure you want to disconnect this credit account? This will remove it from your connected accounts. You can reconnect it later if needed.</div>
            </Modal>
            {
                (mode === 'personal' || (mode !== 'personal' && accountList.length < 1)) && (
                    <Button className="connect-account-btn" onClick={() => { connectAccount() }}>Connect new account</Button>
                )
            }
            {mode === 'team' && accountList.length >= 1 && (
                <div className="team-account-footnote">Your organization uses one team credit account. Remove it first to connect a different vendor.</div>
            )}
        </div>
    )
}

export default CreditAccountList
